import { ref, type Ref } from 'vue'
import axios from 'axios'
import type { LocalGroup, LocalLoginSettings, LocalUser } from '@/types'

export interface LocalIdentityError {
  code: string
  message: string
}

export interface InviteUserInput {
  email: string
  displayName: string
  groupIds: string[]
}

export interface UpdateUserInput {
  displayName?: string
  groupIds?: string[]
}

export interface GroupInput {
  name: string
  description: string
}

const MESSAGES: Record<string, string> = {
  email_taken: 'Diese E-Mail-Adresse wird bereits verwendet.',
  email_not_allowed: 'Diese E-Mail-Adresse kann nicht eingeladen werden.',
  last_admin: 'Mindestens ein aktiver Administrator ist erforderlich.',
  system_group: 'Systemgruppen können nicht gelöscht werden.',
  not_found: 'Der Eintrag wurde nicht gefunden.',
  not_local_mode: 'Die lokale Anmeldung ist für diesen Kunden nicht aktiv.',
  busy: 'Der Kunde wird gerade bearbeitet. Bitte versuchen Sie es gleich erneut.',
  invalid_input: 'Die Eingabe ist ungültig.',
}

const FALLBACK_MESSAGE = 'Die Aktion konnte nicht ausgeführt werden.'

// localIdentityErrorMessage übersetzt den Fehlercode der Registry
// (`{"error":"<code>"}`) in einen deutschen Hinweis.
export function localIdentityErrorMessage(code: string): string {
  return MESSAGES[code] ?? FALLBACK_MESSAGE
}

function mapError(err: unknown): LocalIdentityError {
  let code = ''
  if (axios.isAxiosError(err)) {
    code = (err.response?.data as { error?: string } | undefined)?.error ?? ''
  }
  return { code, message: localIdentityErrorMessage(code) }
}

const CREDENTIALS = { withCredentials: true }

/**
 * useLocalIdentity bündelt Benutzer, Gruppen und Anmelde-Einstellungen der
 * registry-verwalteten lokalen Anmeldung eines Kunden. Schreibaktionen laden
 * danach die betroffene Liste neu und werfen bei Fehlern einen
 * LocalIdentityError mit deutscher Meldung.
 */
export function useLocalIdentity(customerNo: Ref<string>) {
  const users = ref<LocalUser[]>([])
  const groups = ref<LocalGroup[]>([])
  const settings = ref<LocalLoginSettings | null>(null)
  const loading = ref(false)
  const error = ref<LocalIdentityError | null>(null)

  const base = () => `/api/registry/v1/customers/${customerNo.value}`

  async function loadUsers() {
    const resp = await axios.get<LocalUser[]>(`${base()}/local-users`, CREDENTIALS)
    users.value = resp.data ?? []
  }

  async function loadGroups() {
    const resp = await axios.get<LocalGroup[]>(`${base()}/local-groups`, CREDENTIALS)
    groups.value = resp.data ?? []
  }

  async function loadSettings() {
    const resp = await axios.get<LocalLoginSettings>(`${base()}/local-login`, CREDENTIALS)
    settings.value = resp.data
  }

  // load holt zuerst die Einstellungen; Benutzer und Gruppen gibt es nur im
  // lokalen Anmeldemodus. Fehler werden in `error` abgelegt, nicht geworfen.
  async function load() {
    if (!customerNo.value) return
    loading.value = true
    error.value = null
    try {
      await loadSettings()
      if (settings.value?.loginMode === 'local') {
        await Promise.all([loadUsers(), loadGroups()])
      } else {
        users.value = []
        groups.value = []
      }
    } catch (err) {
      error.value = mapError(err)
    } finally {
      loading.value = false
    }
  }

  // reset verwirft den Stand des bisherigen Kunden, bevor ein anderer geladen wird.
  function reset() {
    settings.value = null
    users.value = []
    groups.value = []
    error.value = null
  }

  async function run(action: () => Promise<unknown>, reload: Array<() => Promise<void>>) {
    error.value = null
    try {
      await action()
    } catch (err) {
      const mapped = mapError(err)
      error.value = mapped
      throw mapped
    }
    await Promise.all(reload.map((fn) => fn().catch(() => undefined)))
  }

  const inviteUser = (input: InviteUserInput) =>
    run(() => axios.post(`${base()}/local-users`, input, CREDENTIALS), [
      loadUsers,
      loadGroups,
      loadSettings,
    ])

  const updateUser = (id: string, input: UpdateUserInput) =>
    run(() => axios.put(`${base()}/local-users/${id}`, input, CREDENTIALS), [
      loadUsers,
      loadGroups,
      loadSettings,
    ])

  const setUserEnabled = (id: string, enabled: boolean) =>
    run(
      () => axios.post(`${base()}/local-users/${id}/${enabled ? 'enable' : 'disable'}`, {}, CREDENTIALS),
      [loadUsers, loadSettings],
    )

  const deleteUser = (id: string) =>
    run(() => axios.delete(`${base()}/local-users/${id}`, CREDENTIALS), [
      loadUsers,
      loadGroups,
      loadSettings,
    ])

  const resendInvitation = (id: string) =>
    run(() => axios.post(`${base()}/local-users/${id}/invitation`, {}, CREDENTIALS), [
      loadUsers,
      loadSettings,
    ])

  const createGroup = (input: GroupInput) =>
    run(() => axios.post(`${base()}/local-groups`, input, CREDENTIALS), [loadGroups])

  const updateGroup = (id: string, input: GroupInput) =>
    run(() => axios.put(`${base()}/local-groups/${id}`, input, CREDENTIALS), [loadGroups])

  const setGroupMembers = (id: string, userIds: string[]) =>
    run(
      () => axios.put(`${base()}/local-groups/${id}/members`, { userIds }, CREDENTIALS),
      [loadGroups, loadUsers, loadSettings],
    )

  const deleteGroup = (id: string) =>
    run(() => axios.delete(`${base()}/local-groups/${id}`, CREDENTIALS), [loadGroups, loadUsers])

  async function saveAutoJoin(value: LocalLoginSettings['autoJoin']) {
    await run(() => axios.put(`${base()}/local-login`, { autoJoin: value }, CREDENTIALS), [])
    if (settings.value) settings.value = { ...settings.value, autoJoin: value }
  }

  return {
    users,
    groups,
    settings,
    loading,
    error,
    load,
    reset,
    inviteUser,
    updateUser,
    setUserEnabled,
    deleteUser,
    resendInvitation,
    createGroup,
    updateGroup,
    setGroupMembers,
    deleteGroup,
    saveAutoJoin,
  }
}

export type LocalIdentity = ReturnType<typeof useLocalIdentity>
